import React, { createContext, useReducer, useState, useEffect, useCallback, useContext } from 'react';

import useAsync from 'components/hooks/useAsync';
import { useDispatch } from 'react-redux';

import { getGoalRecurrenceDetails, updateGoalRecurrenceDetails } from 'services/checkIn';
import { showErrorSnackbar, showSuccessSnackbar } from 'services/snackbar';

import { parseRecurrenceData } from '../goalRecurrenceHelpers';

const DAYS = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
export const RECURRENCE_TYPES = {
  week: 'DAY',
  month: {
    DAY_OF_MONTH: 'DAY_OF_MONTH',
    MONTH_FIRST: 'MONTH_FIRST',
    MONTH_SECOND: 'MONTH_SECOND',
    MONTH_THIRD: 'MONTH_THIRD',
    MONTH_FOURTH: 'MONTH_FOURTH',
    MONTH_LAST: 'MONTH_LAST',
  },
  fortnightly: {
    FORTNIGHTLY_DAY: 'FORTNIGHTLY_DAY',
    FORTNIGHTLY_FIRST: 'FORTNIGHTLY_FIRST',
    FORTNIGHTLY_SECOND: 'FORTNIGHTLY_SECOND',
    FORTNIGHTLY_THIRD: 'FORTNIGHTLY_THIRD',
    FORTNIGHTLY_FOURTH: 'FORTNIGHTLY_FOURTH',
    FORTNIGHTLY_LAST: 'FORTNIGHTLY_LAST',
  },
};

const initialRecurrenceState = {
  recurrencePeriod: {
    number: 1,
    period: 'WEEK', // can be WEEK or MONTH or FORTNIGHTLY
  },
  weeklyRecurrenceDetails: {
    day: 1, // 0 is monday, 1 is tuesday
  },
  monthlyRecurrenceDetails: {
    recurrenceType: 'DAY_OF_MONTH',
    dayOfMonth: 1,
    dayOfWeek: 1,
    weekNumber: 0,
  },
  time: {
    hour: 11,
    mins: 0,
  },
  duration: 30,
};

const GoalRecurrenceContext = createContext({});

export const RecurrenceActions = {
  INITIALIZE: 'INITIALIZE',
  UPDATE_RECURRENCE_PERIOD: 'UPDATE_RECURRENCE_PERIOD',
  UPDATE_WEEKLY_RECURRENCE_DAY: 'UPDATE_WEEKLY_RECURRENCE_DAY',
  UPDATE_MONTHLY_RECURRENCE_TYPE: 'UPDATE_MONTHLY_RECURRENCE_TYPE',
  UPDATE_MONTHLY_RECURRENCE_DAY_OF_MONTH: 'UPDATE_MONTHLY_RECURRENCE_DAY_OF_MONTH',
  UPDATE_MONTHLY_RECURRENCE_DAY_OF_WEEK: 'UPDATE_MONTHLY_RECURRENCE_DAY_OF_WEEK',
  UPDATE_MONTHLY_RECURRENCE_WEEK_NUMBER: 'UPDATE_MONTHLY_RECURRENCE_WEEK_NUMBER',
  UPDATE_TIME: 'UPDATE_TIME',
  UPDATE_DURATION: 'UPDATE_DURATION',
};

const recurrenceDetailsReducer = (state, action) => {
  const { type: actionType, payload } = action;
  switch (actionType) {
    case RecurrenceActions.INITIALIZE: {
      return {
        ...payload,
      };
    }
    case RecurrenceActions.UPDATE_RECURRENCE_PERIOD: {
      return {
        ...state,
        recurrencePeriod: {
          ...state.recurrencePeriod,
          period: payload.period,
        },
        weeklyRecurrenceDetails: initialRecurrenceState.weeklyRecurrenceDetails,
        monthlyRecurrenceDetails: initialRecurrenceState.monthlyRecurrenceDetails,
      };
    }
    case RecurrenceActions.UPDATE_WEEKLY_RECURRENCE_DAY: {
      return {
        ...state,
        weeklyRecurrenceDetails: {
          ...state.weeklyRecurrenceDetails,
          day: payload.day,
        },
      };
    }
    case RecurrenceActions.UPDATE_MONTHLY_RECURRENCE_TYPE: {
      return {
        ...state,
        monthlyRecurrenceDetails: {
          ...state.monthlyRecurrenceDetails,
          recurrenceType: payload.recurrenceType,
        },
      };
    }
    case RecurrenceActions.UPDATE_MONTHLY_RECURRENCE_DAY_OF_MONTH: {
      return {
        ...state,
        monthlyRecurrenceDetails: {
          ...state.monthlyRecurrenceDetails,
          dayOfMonth: payload.dayOfMonth,
        },
      };
    }
    case RecurrenceActions.UPDATE_MONTHLY_RECURRENCE_DAY_OF_WEEK: {
      return {
        ...state,
        monthlyRecurrenceDetails: {
          ...state.monthlyRecurrenceDetails,
          dayOfWeek: payload.dayOfWeek,
        },
      };
    }
    case RecurrenceActions.UPDATE_MONTHLY_RECURRENCE_WEEK_NUMBER: {
      return {
        ...state,
        monthlyRecurrenceDetails: {
          ...state.monthlyRecurrenceDetails,
          weekNumber: payload.weekNumber,
        },
      };
    }
    case RecurrenceActions.UPDATE_TIME: {
      return {
        ...state,
        time: payload.time,
      };
    }
    case RecurrenceActions.UPDATE_DURATION: {
      return {
        ...state,
        duration: payload.duration,
      };
    }
    default:
      return state;
  }
};

const GoalRecurrenceDetailsProvider = ({ children, prefetchedData, prefetchDataUpdate, teamId }) => {
  const dispatch = useDispatch();
  const [state, recurrenceDispatch] = useReducer(recurrenceDetailsReducer, initialRecurrenceState);

  const { data: recurrenceDetails, isLoading, isIdle, run, reset } = useAsync();
  // const { currentTask, reloadCheckInList } = useContext(CheckInDataContext);

  const [recurrenceText, setRecurrenceText] = useState(null);

  const populateRecurrenceDetails = useCallback(
    (data) => {
      const { recurrenceType, recurrenceValue } = data;

      // const durationInMinutes = (dueTS - startTS) / 60000;
      let period = 'WEEK';

      if (recurrenceType === 'NONE') {
        period = 'NONE';
      }

      if (Object.values(RECURRENCE_TYPES.month).includes(recurrenceType)) period = 'MONTH';

      if (Object.values(RECURRENCE_TYPES.fortnightly).includes(recurrenceType)) period = 'FORTNIGHTLY';

      // const startDate = new Date(startTS);
      // const hour = getHours(startDate);
      // const mins = getMinutes(startDate);

      const { weeklyRecurrenceDetails, monthlyRecurrenceDetails, recurrenceText } = parseRecurrenceData({
        recurrenceType,
        recurrenceValue,
      });

      setRecurrenceText(recurrenceText);

      recurrenceDispatch({
        type: 'INITIALIZE',
        payload: {
          recurrencePeriod: {
            number: 1,
            period,
          },
          weeklyRecurrenceDetails,
          monthlyRecurrenceDetails,
          // time: { hour, mins },
          // duration: durationInMinutes
        },
      });
    },
    [recurrenceDispatch]
  );

  const fetchData = useCallback(
    () =>
      run(getGoalRecurrenceDetails({ teamId }))
        .then((res) => {
          populateRecurrenceDetails(res);
          // populateRecurrenceDetails({
          //   recurrenceValue: 'WEDNESDAY',
          //   recurrenceType: 'FORTNIGHTLY_THIRD',
          //   teamId: '-1'
          // });
          return res;
        })
        .catch((err) => {
          dispatch(showErrorSnackbar(err.message || 'Failed to fetch recurrence details.'));
        }),
    [teamId, run]
  );

  useEffect(() => {
    !prefetchedData && fetchData();
    prefetchedData && populateRecurrenceDetails(prefetchedData);
  }, [fetchData, prefetchedData]);

  const resetContext = useCallback(() => {
    !prefetchedData && populateRecurrenceDetails(recurrenceDetails);
    prefetchedData && populateRecurrenceDetails(prefetchedData);
  }, [recurrenceDetails, populateRecurrenceDetails, prefetchedData]);

  const getRecurrenceValue = () => {
    if (state.recurrencePeriod?.period === 'NONE') {
      return 'NONE';
    }
    if (state.recurrencePeriod?.period === 'WEEK') {
      return DAYS[state.weeklyRecurrenceDetails.day];
    }
    if (state.recurrencePeriod?.period === 'FORTNIGHTLY') {
      return DAYS[state.monthlyRecurrenceDetails.dayOfWeek];
    }
    return DAYS[state.monthlyRecurrenceDetails.dayOfWeek];
  };

  const submit = () => {
    const originalRecurrenceDetails = prefetchedData || recurrenceDetails;

    let recurrenceType = originalRecurrenceDetails?.recurrenceType;
    // let startTS = originalRecurrenceDetails?.startTS;
    // let dueTS = originalRecurrenceDetails?.dueTS;

    // WHEN NONE IS SELECTED

    // WHEN WEEK IS SELECTED
    if (state.recurrencePeriod?.period === 'WEEK') {
      recurrenceType = 'DAY';
      // date = setDay(date, state.weeklyRecurrenceDetails?.day);
    } else if (state.recurrencePeriod?.period === 'NONE') {
      recurrenceType = 'NONE';
    } else if (state.recurrencePeriod?.period === 'FORTNIGHTLY') {
      // WHEN FORTNIGHTLY IS SELECTED
      if (!state.monthlyRecurrenceDetails) return;

      // date = setMonth(date, 2);

      const { weekNumber } = state.monthlyRecurrenceDetails;

      // date = setWeek(date, weekNumber);
      // date = setDay(date, dayOfWeek);

      switch (weekNumber) {
        case 0:
          recurrenceType = 'FORTNIGHTLY_FIRST';
          break;
        case 1:
          recurrenceType = 'FORTNIGHTLY_SECOND';
          break;
        case 2:
          recurrenceType = 'FORTNIGHTLY_THIRD';
          break;
        case 3:
          recurrenceType = 'FORTNIGHTLY_FOURTH';
          break;
        default:
          recurrenceType = 'FORTNIGHTLY_LAST';
      }
    } else {
      // WHEN MONTHLY IS SELECTED
      if (!state.monthlyRecurrenceDetails) return;

      // date = setMonth(date, 2);

      const { weekNumber } = state.monthlyRecurrenceDetails;

      // date = setWeek(date, weekNumber);
      // date = setDay(date, dayOfWeek);

      switch (weekNumber) {
        case 0:
          recurrenceType = 'MONTH_FIRST';
          break;
        case 1:
          recurrenceType = 'MONTH_SECOND';
          break;
        case 2:
          recurrenceType = 'MONTH_THIRD';
          break;
        case 3:
          recurrenceType = 'MONTH_FOURTH';
          break;
        default:
          recurrenceType = 'MONTH_LAST';
      }
    }

    // date = setHours(date, state?.time?.hour);
    // date = setMinutes(date, state?.time?.mins);

    // startTS = date.getTime();
    // dueTS = startTS + state.duration * 60 * 1000;

    const dataObj = {
      teamId,
      recurrenceType,
      recurrenceValue: getRecurrenceValue(),
    };
    updateGoalRecurrenceDetails(dataObj)
      .then((res) => {
        reset();
        if (prefetchedData) {
          prefetchDataUpdate(res);
        } else {
          // reloadCheckInList();
          fetchData();
        }
        dispatch(showSuccessSnackbar('Successfully updated check-in reminder frequency!'));
      })
      .catch((err) => {
        dispatch(showErrorSnackbar('Failed to update recurrence details.'));
        console.error(err.message || 'Failed to update recurrence details.');
      });
  };

  const getFinalValue = () => {
    const originalRecurrenceDetails = prefetchedData || recurrenceDetails;

    let recurrenceType = originalRecurrenceDetails?.recurrenceType;
    // let startTS = originalRecurrenceDetails?.startTS;
    // let dueTS = originalRecurrenceDetails?.dueTS;

    // WHEN NONE IS SELECTED

    // WHEN WEEK IS SELECTED
    if (state.recurrencePeriod?.period === 'WEEK') {
      recurrenceType = 'DAY';
      // date = setDay(date, state.weeklyRecurrenceDetails?.day);
    } else if (state.recurrencePeriod?.period === 'NONE') {
      recurrenceType = 'NONE';
    } else if (state.recurrencePeriod?.period === 'FORTNIGHTLY') {
      // WHEN FORTNIGHTLY IS SELECTED
      if (!state.monthlyRecurrenceDetails) return;

      // date = setMonth(date, 2);

      const { weekNumber } = state.monthlyRecurrenceDetails;

      // date = setWeek(date, weekNumber);
      // date = setDay(date, dayOfWeek);

      switch (weekNumber) {
        case 0:
          recurrenceType = 'FORTNIGHTLY_FIRST';
          break;
        case 1:
          recurrenceType = 'FORTNIGHTLY_SECOND';
          break;
        case 2:
          recurrenceType = 'FORTNIGHTLY_THIRD';
          break;
        case 3:
          recurrenceType = 'FORTNIGHTLY_FOURTH';
          break;
        default:
          recurrenceType = 'FORTNIGHTLY_LAST';
      }
    } else {
      // WHEN MONTHLY IS SELECTED
      if (!state.monthlyRecurrenceDetails) return;

      // date = setMonth(date, 2);

      const { weekNumber } = state.monthlyRecurrenceDetails;

      // date = setWeek(date, weekNumber);
      // date = setDay(date, dayOfWeek);

      switch (weekNumber) {
        case 0:
          recurrenceType = 'MONTH_FIRST';
          break;
        case 1:
          recurrenceType = 'MONTH_SECOND';
          break;
        case 2:
          recurrenceType = 'MONTH_THIRD';
          break;
        case 3:
          recurrenceType = 'MONTH_FOURTH';
          break;
        default:
          recurrenceType = 'MONTH_LAST';
      }
    }

    // date = setHours(date, state?.time?.hour);
    // date = setMinutes(date, state?.time?.mins);

    // startTS = date.getTime();
    // dueTS = startTS + state.duration * 60 * 1000;

    const dataObj = {
      teamId,
      recurrenceType,
      recurrenceValue: getRecurrenceValue(),
    };
    return dataObj;
  };

  return (
    <GoalRecurrenceContext.Provider
      value={{
        state,
        recurrenceText,
        reset: resetContext,
        submit,
        recurrenceDispatch,
        loading: isLoading || isIdle,
        getFinalValue,
      }}
    >
      {children}
    </GoalRecurrenceContext.Provider>
  );
};

export default GoalRecurrenceDetailsProvider;
export const useGoalRecurrenceDetails = () => useContext(GoalRecurrenceContext);
